// src/Components/Widgets/CheckInOutModal.jsx
import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button } from "@mui/material";
import dayjs from "dayjs";
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AttendanceService from "../../../Services/attendance.service";
import MonthlyScheduleModal from "../MonthlyScheduleModal/MonthlyScheduleModal";
import { t } from "i18next";
import { useTranslation } from "react-i18next";

const dialogStyle = {
    textAlign: "center",
    minWidth: 300,
};

export default function CheckInOutModal({ open, onClose, accountantId, authToken, t }) {
    const { i18n } = useTranslation();
    const direction = i18n.language === 'he' || i18n.language === 'ar' ? 'rtl' : 'ltr';
    const [now, setNow] = useState(dayjs());
    const [checkedIn, setCheckedIn] = useState(false);
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    // Live clock
    useEffect(() => {
        if (!open) return;
        const timer = setInterval(() => setNow(dayjs()), 1000);
        return () => clearInterval(timer);
    }, [open]);

    // Fetch today's status
    useEffect(() => {
        if (!open) return;
        const timeStringUtc = now.format();

        AttendanceService
            .CheckStatus(authToken, timeStringUtc)
            .then(res => res.json())
            .then(({ isCheckedIn }) => setCheckedIn(isCheckedIn))
            .catch(console.error);
    }, [open, accountantId, authToken]);

    const handlePress = async () => {
        const timeString = now.format(); // or now.format() but ISO is easiest
        
        const call = checkedIn
            ? AttendanceService.CheckOut
            : AttendanceService.CheckIn;

        if (checkedIn) {
            localStorage.setItem("checkedIn", null);  
        } else {
            localStorage.setItem("checkedIn", timeString);  
        }
        try {
            const res = await call(accountantId, authToken, timeString);
            if (res.ok) setCheckedIn(!checkedIn);
        } catch (e) {
            console.error(e);
        } finally {
            onClose();
        }
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                dir={direction}
                PaperProps={{
                    sx: dialogStyle
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <AccessAlarmIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                </DialogTitle>
                
                <DialogContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                        {t('CheckInShiftInfo')}
                    </Typography>
                    <Typography variant="h3" sx={{ fontFamily: 'Monospace', mb: 3 }}>
                        {now.format('HH:mm:ss')}
                    </Typography>
                </DialogContent>
                
                <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 3 }}>
                    <Button
                        variant="contained"
                        size="large"
                        color={checkedIn ? 'warning' : 'primary'}
                        dir={direction}
                        sx={{ borderRadius: 2, py: 1.5, fontSize: '1rem', width: '100%' }}
                        onClick={handlePress}
                    >
                        {checkedIn ? t('CheckOut') : t('CheckIn')}
                    </Button>
                    
                    <Button
                        variant="outlined"
                        size="medium"
                        startIcon={<CalendarMonthIcon />}
                        dir={direction}
                        sx={{ borderRadius: 2, py: 1, fontSize: '0.9rem', width: '100%' }}
                        onClick={() => setScheduleModalOpen(true)}
                    >
                        {t('MonthlySchedule') || 'Monthly Schedule'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            <MonthlyScheduleModal
                open={scheduleModalOpen}
                onClose={() => setScheduleModalOpen(false)}
                authToken={authToken}
                year={now.year()}
                month={now.month() + 1}
            />
        </>
    );
}
